

























// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';

// services
import { useService } from '@/vue3/api/services';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useGeometryHoverHook } from '@/vue3/hooks/geometryHoverHook';
import { useNotify } from '@/vue3/hooks/notifyHook';

// primevue
import Button from 'primevue/button';

// turf
import * as turf from '@turf/turf';

// configs
import geoRecordLayers from '@/vue3/configs/geoRecordLayers';

export default defineComponent({
    components: {
        Button,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        setMapboxDrawSnap: {
            type: Function,
            default: () => {},            
        },
        geoRecordsArray: {
            type: Array,
            default: () => [],
        },
        cancelSelectByMultiTool: {
            type: Function,
            default: () => {},
        },
        changeDrawMode: {
            type: Function,
            default: () => {},
        },
        toggleIsLoading: {
            type: Function,
            default: () => {},
        },
    },
    setup(props: any) {
        // hooks
        const {
            initDuplicateSource,
            removeDuplicateSource,
            addGeoRecord,
            removeCursorSource,
        } = useGeoRecord();
        const { highlightGeometry, highlightGeoRecordExtrusions } =
            useGeometryHoverHook();
        const { errorToast, successToast } = useNotify();

        // services
        const { createMultipleGeoRecords } = useService();

        // refs
        const cursorLocation = ref([] as Array<number>);
        const centroid = ref<object | undefined>({});
        const selectedGeoRecords = ref<any[]>([]);
        const duplicatedGeoRecords = ref<any[]>([]);

        const isSnappingActivated = ref(false);
        const isGeometrySnappable = ref(false);

        const lastDuplicatedRecordMap = new Map();

        // methods
        const clearGeoRecordSelected = (): void => {
            removeDuplicateSource(props.mapbox.map);
            activateSimpleSelect();

            isSnappingActivated.value = false;
        };

        const onMouseMove = (e: any): void => {
            highlightGeometry(e, props.mapbox.map, 'geo-records');
        };

        const hoverExtrusion = (e: any): void => {
            if (!props.mapbox.map.getLayer('polygon-extrusion')) return;

            highlightGeoRecordExtrusions(
                e,
                props.mapbox.map,
                'polygon-extrusion',
            );
        };

        const activateMouseMove = (): void => {
            props.mapbox.map.on('mousemove', hoverExtrusion);

            geoRecordLayers.forEach(({ id }) => {
                props.mapbox.map.on('mousemove', id, onMouseMove);
                props.mapbox.map.on('mouseleave', id, onMouseMove);
            });
        };

        const deActivateMouseMove = (): void => {
            props.mapbox.map.off('mousemove', hoverExtrusion);

            geoRecordLayers.forEach(({ id }) => {
                props.mapbox.map.off('mousemove', id, onMouseMove);
                props.mapbox.map.off('mouseleave', id, onMouseMove);
            });
        };

        const updateCursorLngLat = (snapFeature: any): Array<number> => {
            return snapFeature.geometry.type === 'LineString'
                ? [
                      snapFeature.geometry.coordinates[0][0],
                      snapFeature.geometry.coordinates[0][1],
                  ]
                : [
                      snapFeature.geometry.coordinates[0][0][0],
                      snapFeature.geometry.coordinates[0][0][1],
                  ];
        };

        const getCentroid = (
            geometry: any,
            geometryType: string,
        ): Array<number> => {
            return geometryType === 'LineString'
                ? geometry.coordinates[0]
                : geometry.coordinates[0][0];
        };

        const snapCursor = (feature: any): void => {
            let snapFeature = props.mapbox.draw.getAll().features[0];

            if (!snapFeature) return;

            cursorLocation.value = updateCursorLngLat(snapFeature);
            centroid.value = getCentroid(
                feature.geometry,
                snapFeature.geometry.type,
            );
        };

        const toggleSnapButtonVisibility = (): boolean => {
            return props.geoRecordsArray.forEach((geoRecord: any) => {
                geoRecord?.geometry.type === 'Polygon' ||
                geoRecord?.geometry.type === 'LineString'
                    ? true
                    : false;
            });
        };

        const captureInitialSelection = (): void => {
            selectedGeoRecords.value = props.geoRecordsArray;
        };

        const initDuplication = async (e: any) => {
            const features = props.mapbox.map.queryRenderedFeatures(e.point);

            if (features.length === 0) return;

            const clickedFeatureId = features[0].id;

            captureInitialSelection();

            const selectedFeature = selectedGeoRecords.value.find(
                (geoRecord) => geoRecord.id === clickedFeatureId,
            );

            if (!selectedFeature) return;

            isGeometrySnappable.value = toggleSnapButtonVisibility();
            removeDuplicateSource(props.mapbox.map);
            initDuplicateSource(props.mapbox.map, selectedGeoRecords.value);
            deActivateMouseMove();
        };

        const prepareMove = (e: any) => {
            const seenIds = new Set();
            const featuresArray: any = [];

            selectedGeoRecords.value.forEach((feature) => {
                if (!seenIds.has(feature.id)) {
                    seenIds.add(feature.id);
                    featuresArray.push(feature);
                }
            });

            return featuresArray.map((feature: any) => {
                const source = props.mapbox.map.getSource('temp-geo-records');
                const featureCollection = source._data;

                const recordFeature = featureCollection.features.find(
                    (f: any) => f.id === feature.id,
                );

                if (!recordFeature) return null;

                cursorLocation.value = e.lngLat.toArray();
                const centroidCoords =
                    turf.centroid(recordFeature).geometry?.coordinates;

                if (!centroidCoords) return null;

                snapCursor(recordFeature);

                return {
                    source,
                    feature: recordFeature,
                    centroidCoords,
                };
            });
        };

        const moveGeoRecordLayer = (e: any): void => {
            const preparedFeatures = prepareMove(e);

            const initialCentroid = preparedFeatures[0].centroidCoords;

            preparedFeatures.forEach(({ source, feature, centroidCoords }) => {
                if (!source || !feature) return;

                const deltaX = cursorLocation.value[0] - initialCentroid[0];
                const deltaY = cursorLocation.value[1] - initialCentroid[1];

                const translatedFeature = translate(feature, centroidCoords, [
                    centroidCoords[0] + deltaX,
                    centroidCoords[1] + deltaY,
                ]);

                const newFeatureCollection = {
                    type: 'FeatureCollection',
                    features: source._data.features.map((f) =>
                        f.id === feature.id ? translatedFeature : f,
                    ),
                };

                source.setData(newFeatureCollection);

                lastDuplicatedRecordMap.set(translatedFeature.id, {
                    ...translatedFeature.properties,
                    geometry: translatedFeature.geometry,
                });

                duplicatedGeoRecords.value = Array.from(
                    lastDuplicatedRecordMap.values(),
                );
            });
        };

        const translate = (
            feature: any,
            initialClick: any,
            currentCursor: any,
        ) => {
            const deltaX = currentCursor[0] - initialClick[0];
            const deltaY = currentCursor[1] - initialClick[1];

            let translatedCoordinates;

            if (feature.geometry.type === 'Point') {
                translatedCoordinates = [
                    feature.geometry.coordinates[0] + deltaX,
                    feature.geometry.coordinates[1] + deltaY,
                ];
            } else {
                translatedCoordinates = feature.geometry.coordinates.map(
                    (coord) => {
                        if (Array.isArray(coord[0])) {
                            return coord.map((subCoord) => [
                                subCoord[0] + deltaX,
                                subCoord[1] + deltaY,
                            ]);
                        } else {
                            return [coord[0] + deltaX, coord[1] + deltaY];
                        }
                    },
                );
            }

            return {
                ...feature,
                geometry: {
                    ...feature.geometry,
                    coordinates: translatedCoordinates,
                },
            };
        };

        const activateSimpleSelect = (): void => {
            props.mapbox.map.getCanvas().style.cursor = '';
            props.mapbox.draw.changeMode('simple_select');
            removeCursorSource(props.mapbox.map);
        };

        const activateSnapping = (): void => {
            if (!isSnappingActivated.value) return;

            selectedGeoRecords.value.forEach((selectedGeoRecord) => {
                selectedGeoRecord.geometry.type === 'Polygon'
                    ? props.mapbox.draw.changeMode('SnapPolygonMode')
                    : props.mapbox.draw.changeMode('SnapLineStringMode');
            });

            // props.mapbox.draw.options.snap = isSnappingActivated.value;
            props.setMapboxDrawSnap(isSnappingActivated.value)
        };

        const onGeoRecordClick = async (e: any) => {
            activateSimpleSelect();
            activateSnapping();

            await initDuplication(e);

            props.mapbox.map.on('mousemove', moveGeoRecordLayer);
            props.mapbox.map.on('contextmenu', saveDuplicatedRecords);
        };

        const prepareGeoRecordFormData = () => {
            let fd = new FormData();
            let limit = 10;
            if (duplicatedGeoRecords.value.length <= limit) {
                duplicatedGeoRecords.value.forEach(
                    (duplicatedRecord, recordIndex) => {
                        fd.append(
                            `json-${recordIndex}`,
                            JSON.stringify(duplicatedRecord),
                        );
                        if (duplicatedRecord.images) {
                            duplicatedRecord.images.forEach(
                                (geoRecordImage, imageIndex) => {
                                    fd.append(
                                        `image${recordIndex}-${imageIndex}`,
                                        geoRecordImage.image,
                                    );
                                    if (geoRecordImage.date) {
                                        let dateString =
                                            '' + +geoRecordImage.date;
                                        let shortenedDateString =
                                            dateString.slice(
                                                0,
                                                dateString.length - 3,
                                            );
                                        fd.append(
                                            `image-date${recordIndex}-${imageIndex}`,
                                            shortenedDateString,
                                        );
                                    } else {
                                        fd.append(
                                            `image-date${recordIndex}-${imageIndex}`,
                                            JSON.stringify(geoRecordImage.date),
                                        );
                                    }
                                },
                            );
                        }
                    },
                );
                return fd;
            } else {
                return errorToast(
                    `Cannot duplicate more than ${limit} records in one request`,
                );
            }
        };

        const saveDuplicatedRecords = async (e: any) => {
            props.toggleIsLoading(true);
            e.preventDefault();

            let formData = prepareGeoRecordFormData();
            try {
                if (formData) {
                    const newGeoRecords = await createMultipleGeoRecords(
                        formData,
                    );

                    newGeoRecords.forEach((geoRecord: any) => {
                        addGeoRecord(props.mapbox.map, geoRecord);
                    });
                    duplicatedGeoRecords.value = [];

                    successToast('Successfully duplicated geo records.');
                }
            } catch (error) {
                window.console.log(error);
            }

            clearSelection();
            deActivateDuplicateTool();
            props.toggleIsLoading(false);
        };

        const activateDuplicateTool = (): void => {
            activateMouseMove();

            props.mapbox.map.on('click', onGeoRecordClick);
        };

        const deActivateDuplicateTool = (): void => {
            clearGeoRecordSelected();
            deActivateMouseMove();

            props.mapbox.map.off('click', onGeoRecordClick);
            props.mapbox.map.off('mousemove', moveGeoRecordLayer);
            props.mapbox.map.off('contextmenu', saveDuplicatedRecords);
        };

        const clearSelection = (): void => {
            props.cancelSelectByMultiTool();
            props.changeDrawMode();
        };

        // lifecycle
        onMounted(() => {
            activateDuplicateTool();
        });

        onUnmounted(() => {
            deActivateDuplicateTool();
        });

        return {
            isSnappingActivated,
            isGeometrySnappable,
            clearGeoRecordSelected,
            activateSnapping,
            clearSelection,
        };
    },
});
